// Small modal to edit two company-identity fields (company_name + website)
// from anywhere in the app — intake sidebar or Business Brain profile.
// Reuses the intake validation helper's spirit. No new backend route:
// hits the existing PATCH /business-brain/profile (which now upserts +
// mirrors into intake_sessions.answers).
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { X } from "lucide-react";

const DOMAIN_RE = /^(https?:\/\/)?([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(\/.*)?$/i;

function validate(company_name, website) {
  const errs = {};
  if (!company_name || company_name.trim().length < 2) {
    errs.company_name = "Bedrijfsnaam moet minimaal 2 tekens bevatten.";
  }
  if (website && website.trim()) {
    if (!DOMAIN_RE.test(website.trim())) {
      errs.website = "Vul een geldig webadres in (bijv. voorbeeld.nl).";
    }
  }
  return errs;
}

export function EditCompanyBasicsModal({ open, onClose, initial, onSaved }) {
  const [company_name, setCompanyName] = useState(initial?.company_name || "");
  const [website, setWebsite] = useState(initial?.website || "");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setCompanyName(initial?.company_name || "");
      setWebsite(initial?.website || "");
      setErrors({}); setError(null);
    }
  }, [open, initial?.company_name, initial?.website]);

  if (!open) return null;

  const dirty = (initial?.company_name || "") !== company_name.trim()
             || (initial?.website || "") !== website.trim();

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(company_name.trim(), website.trim());
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!dirty) { onClose(); return; }
    setSaving(true); setError(null);
    try {
      const payload = { company_name: company_name.trim() };
      if (website.trim()) payload.website = website.trim();
      const { data } = await api.patch("/business-brain/profile", payload);
      if (onSaved) onSaved(data.profile);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-company-modal"
      className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-panel rounded-xl shadow-xl border border-border-hairline w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-text-primary tracking-tight">
              Bedrijfsgegevens wijzigen
            </h2>
            <p className="text-xs text-text-muted mt-0.5">
              Uw intake-voortgang en andere antwoorden blijven behouden.
            </p>
          </div>
          <button
            data-testid="edit-company-modal-close"
            onClick={onClose}
            className="text-text-subtle hover:text-text-primary/85"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="edit-company-name" className="text-xs text-text-muted font-semibold">
              Bedrijfsnaam
            </label>
            <input
              id="edit-company-name"
              data-testid="edit-company-name-input"
              value={company_name}
              onChange={(e) => { setCompanyName(e.target.value); if (errors.company_name) setErrors({ ...errors, company_name: null }); }}
              placeholder="Bijv. Vloerspecialist Amsterdam BV"
              className="mt-1 w-full h-11 px-4 rounded-md bg-bg-panel border border-border-hairline text-text-primary placeholder:text-text-subtle focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/30"
            />
            {errors.company_name && (
              <div data-testid="edit-company-name-error" className="mt-1 text-xs text-neg-red">
                {errors.company_name}
              </div>
            )}
          </div>
          <div>
            <label htmlFor="edit-company-website" className="text-xs text-text-muted font-semibold">
              Website <span className="text-text-subtle italic normal-case">(optioneel)</span>
            </label>
            <input
              id="edit-company-website"
              data-testid="edit-company-website-input"
              value={website}
              onChange={(e) => { setWebsite(e.target.value); if (errors.website) setErrors({ ...errors, website: null }); }}
              placeholder="Bijv. voorbeeld.nl of https://voorbeeld.nl"
              className="mt-1 w-full h-11 px-4 rounded-md bg-bg-panel border border-border-hairline text-text-primary placeholder:text-text-subtle focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/30"
            />
            {errors.website && (
              <div data-testid="edit-company-website-error" className="mt-1 text-xs text-neg-red">
                {errors.website}
              </div>
            )}
          </div>
          {error && (
            <div data-testid="edit-company-generic-error" className="text-xs text-neg-red">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="edit-company-cancel-btn"
              onClick={onClose}
              className="h-10 px-4 rounded-md border border-border-hairline text-sm font-semibold text-text-primary/85 hover:bg-bg-panel"
            >
              Annuleren
            </button>
            <button
              type="submit"
              data-testid="edit-company-save-btn"
              disabled={saving}
              className="h-10 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white text-sm font-semibold"
            >
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
